import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { clientConfirmReset } from "../lib/clientAuth";

// Confirm a password reset. Reads ?token=... from the URL, immediately
// scrubs it from history so browser bookmarks / back-button never leak it.

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const raw = params.get("token") || "";
    setToken(raw);
    // Remove the token from the URL so it isn't kept in browser history.
    if (raw) window.history.replaceState({}, "", "/reset-password");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !token || password.length < 8) return;
    setBusy(true);
    try {
      await clientConfirmReset(token, password);
      setDone(true);
      setTimeout(() => navigate("/audit/login", { replace: true }), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't reset your password. The link may have expired.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-base flex flex-col">
      <Toaster richColors position="top-center" />
      <BrandHeader small />

      <main className="flex-1 px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-md mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {done ? (
            <div data-testid="reset-success">
              <CheckCircle2 size={40} className="text-brand-primary" strokeWidth={1.5} />
              <h1 className="font-serif text-3xl text-brand-ink mt-4">Password updated</h1>
              <p className="mt-3 text-brand-ink-muted">Taking you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl text-brand-ink">Choose a new password</h1>
              {!token && (
                <p className="mt-3 text-red-600" data-testid="reset-missing-token">
                  This reset link is missing or invalid. <Link to="/forgot-password" className="underline">Request a new link.</Link>
                </p>
              )}
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="label-eyebrow block mb-2" htmlFor="new-pw">New password</label>
                  <input
                    id="new-pw"
                    type="password"
                    autoComplete="new-password"
                    className="input-warm text-lg"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    autoFocus
                  />
                  <p className="mt-2 text-sm text-brand-ink-muted">
                    Minimum 8 characters, with an uppercase letter, lowercase letter and a number.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={!token || password.length < 8 || busy}
                  className="btn-primary inline-flex items-center gap-2 w-full sm:w-auto disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Updating…</> : "Set new password"}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
